import React, { useState, useEffect, useCallback } from "react";
import { adminService } from "../../services/adminService";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import toast from "react-hot-toast";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import LogoLoader from "../Common/LogoLoader";

const PAGE_SIZE = 20;

const NIGERIAN_STATES = [
  "Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue", "Borno",
  "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu", "Gombe", "Imo",
  "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi", "Kwara", "Lagos",
  "Nasarawa", "Niger", "Ogun", "Ondo", "Osun", "Oyo", "Plateau", "Rivers",
  "Sokoto", "Taraba", "Yobe", "Zamfara", "Abuja",
];

const AgentManagementAdmin = () => {
  const [tab, setTab] = useState("pending");
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 450);
  const [stateFilter, setStateFilter] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ totalPages: 1, totalItems: 0 });
  const [editAgent, setEditAgent] = useState(null);
  const [form, setForm] = useState({
    verificationMethod: "NIN",
    verificationNumber: "",
    phone_number: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        state: stateFilter || undefined,
      };
      const data =
        tab === "pending"
          ? await adminService.listPendingAgentRequests(params)
          : await adminService.listApprovedAgentsAdmin(params);
      setRows(tab === "pending" ? data.users || [] : data.agents || []);
      setPagination(data.pagination || { totalPages: 1, totalItems: 0 });
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [tab, page, debouncedSearch, stateFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [tab, debouncedSearch, stateFilter]);

  const approve = async (id) => {
    try {
      await adminService.approveAgentRequest(id);
      toast.success("Agent approved — email sent to user");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Approve failed");
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Reject this application?")) return;
    try {
      await adminService.rejectAgentRequest(id);
      toast.success("Application rejected");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Reject failed");
    }
  };

  const openEdit = (u) => {
    setEditAgent(u);
    const d = u.agentDetails || {};
    setForm({
      verificationMethod: d.verificationMethod || "NIN",
      verificationNumber: d.verificationNumber || "",
      phone_number: d.phone_number || u.phoneNumber || "",
    });
  };

  const saveDetails = async (e) => {
    e.preventDefault();
    if (!editAgent) return;
    setSaving(true);
    try {
      await adminService.updateAgentDetailsAdmin(editAgent._id, {
        verificationMethod: form.verificationMethod,
        verificationNumber: form.verificationNumber,
        phone_number: form.phone_number,
      });
      toast.success("Agent details updated");
      setEditAgent(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Agent management</h1>
        <p className="text-gray-600 mt-1">Review new applications and manage approved agents.</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
        <button
          type="button"
          onClick={() => setTab("pending")}
          className={`px-4 py-2 rounded-t-lg font-medium ${
            tab === "pending" ? "bg-green-600 text-white" : "bg-gray-100 text-gray-700"
          }`}
        >
          Pending registration
        </button>
        <button
          type="button"
          onClick={() => setTab("approved")}
          className={`px-4 py-2 rounded-t-lg font-medium ${
            tab === "approved" ? "bg-green-600 text-white" : "bg-gray-100 text-gray-700"
          }`}
        >
          Approved agents
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 flex flex-col md:flex-row gap-4">
        <input
          type="search"
          placeholder="Search name, email, phone, verification…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="flex-1 border border-gray-300 rounded-md px-3 py-2"
        />
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2"
        >
          <option value="">All states</option>
          {NIGERIAN_STATES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {loading && !rows.length ? (
        <div className="flex justify-center h-48 items-center">
          <LogoLoader />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verification</th>
                  {tab === "pending" ? (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  ) : (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Manage</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((u) => {
                  const d = u.agentDetails || {};
                  return (
                    <tr key={u._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">
                          {d.agent_name || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username}
                        </div>
                        <div className="text-gray-500 text-xs">@{u.username}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{u.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{d.phone_number || u.phoneNumber}</td>
                      <td className="px-4 py-3 text-sm">{u.state}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {d.verificationMethod || "—"}: {d.verificationNumber || "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {tab === "pending" ? (
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => approve(u._id)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-800 text-sm"
                            >
                              <CheckCircleIcon className="h-4 w-4" /> Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => reject(u._id)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-100 text-red-800 text-sm"
                            >
                              <XCircleIcon className="h-4 w-4" /> Reject
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openEdit(u)}
                            className="inline-flex items-center gap-1 text-green-600 text-sm font-medium"
                          >
                            <PencilIcon className="h-4 w-4" /> Edit details
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rows.length === 0 && !loading && (
            <p className="text-center py-8 text-gray-500">No records.</p>
          )}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Page {pagination.currentPage || page} of {pagination.totalPages || 1} · {pagination.totalItems ?? 0}{" "}
              items
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center px-3 py-1.5 border rounded-md disabled:opacity-40"
              >
                <ChevronLeftIcon className="h-4 w-4" /> Prev
              </button>
              <button
                type="button"
                disabled={page >= (pagination.totalPages || 1)}
                onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center px-3 py-1.5 border rounded-md disabled:opacity-40"
              >
                Next <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {editAgent && tab === "approved" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Edit agent verification</h2>
            <form onSubmit={saveDetails} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Verification method</label>
                <select
                  value={form.verificationMethod}
                  onChange={(e) => setForm((f) => ({ ...f, verificationMethod: e.target.value }))}
                  className="mt-1 w-full border rounded-md px-3 py-2"
                >
                  <option value="NIN">NIN</option>
                  <option value="Voters card">Voters card</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Verification number</label>
                <input
                  type="text"
                  value={form.verificationNumber}
                  onChange={(e) => setForm((f) => ({ ...f, verificationNumber: e.target.value }))}
                  className="mt-1 w-full border rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Agent phone</label>
                <input
                  type="text"
                  value={form.phone_number}
                  onChange={(e) => setForm((f) => ({ ...f, phone_number: e.target.value }))}
                  className="mt-1 w-full border rounded-md px-3 py-2"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditAgent(null)} className="px-4 py-2 border rounded-md">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-md disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentManagementAdmin;
